import '../../../../../components/dialogs/manage-product-technical-properties/index.scss';

import {Box, Card, Checkbox, Chip, CircularProgress, Fade, FormControlLabel, IconButton, Skeleton, TextField, Typography} from "@mui/material";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import Button from "../../../../../components/base/button/button";
import Grid from "@mui/material/Unstable_Grid2";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {Add, ArrowRightAltOutlined, DeleteOutlined, DragHandleRounded, EditOutlined, ListAltOutlined, OpenInNew, WarningOutlined} from "@mui/icons-material";
import UpsertProductTechnicalPropertyDialog from "../../../../../components/dialogs/upsert-product-technical-property";
import SuggestTechnicalPropertyDialog from "../../../../../components/dialogs/suggest-technical-property";
import Utils, {stringComparator} from "../../../../../../core/services/utils/utils";
import {setReduxDialogState} from "../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../core/models/static/routes/redux-store-routes";
import {useDispatch} from "react-redux";
import DownloadAffectedProductsDialog from "../../../../../components/dialogs/download-affected-products";
import {ReactComponent as SaveIcon} from "../../../../../../assets/images/save-rollback/check.svg";
import {ReactComponent as CancelIcon} from "../../../../../../assets/images/save-rollback/cancel.svg";
import classnames from "classnames";
import Api from "../../../../../../core/services/api/api";
import axios from "axios";
import {ProductTechnicalPropertyTypeIds} from "../../../../../../core/constants/enums";
import useIndustry from "../../../../../hooks/use-industry";
import {Tab, Tabs} from "../../../../../components/base/Tabs";
import LoadingLottieAnimation from "../../../../../components/app-specific/lottie-animations/loading";
import {ManageTechnicalPropertyTemplatesDialog} from "./templates-management";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import {Link} from "react-router-dom";
import {routes} from "../../../../../../core/models/static/routes/app-routes";
import {PanelFooter} from "../../../../../components/layout";
import {ReactSortable} from "react-sortablejs";
import ChangeOrderDialog from "../../../../../components/dialogs/change-order";
import useAccessLevels from "../../../../../hooks/use-access-levels";

const initialSuggestTechnicalPropertyDialogState = {open: false};
const initialAddCustomPropertyDialogState = {open: false,}
const initialManageTechnicalPropertyTemplatesDialogState = {open: false};
const initialUpdatePropertyDialogState = {open: false, property: null};
const initialAffectedProductsDialogState = {open: false, productsList: [], message: '', fileTitle: ''};

const initChangeOrderDialogState = {
    open: false,
    close: () => void 0,
    orderIndex: 0,
    length: 0,
    reorder: () => void 0,
    message: '',
}


const ManageProductPropertiesView = () => {
    const {industries, cachedIndustry, fetchedIndustry, fetchIndustry, loadingIndustries, hasNoIndustries} = useIndustry({noCategories: true});
    const industry = React.useMemo(() => fetchedIndustry, [fetchedIndustry]);

    const operatorAccessLevels = useAccessLevels();
    const [loadingProperties, setLoadingProperties] = useState(false);
    const [_properties, setProperties] = useState([]);
    const [propertyExtraConfig, setPropertyExtraConfig] = useState({
        highlightedProperties: [],
        propertiesOrders: [],
        industryProperties: [],
        variants: [],
    })
    const [keywordValue, setKeywordValue] = useState('');
    const [searchQuery, setSearchQuery] = useState({keyword: ''});
    const [, startTransition] = React.useTransition()

    const [suggestTechnicalPropertyDialogState, setSuggestTechnicalPropertyDialogState] = useState(initialSuggestTechnicalPropertyDialogState);
    const [addCustomPropertyDialogState, setAddCustomPropertyDialogState] = useState(initialAddCustomPropertyDialogState);
    const [updatePropertyDialogState, setUpdatePropertyDialogState] = useState(initialUpdatePropertyDialogState);
    const [manageTechnicalPropertyTemplatesDialogState, setManageTechnicalPropertyTemplatesDialogState] = useState(initialManageTechnicalPropertyTemplatesDialogState);
    const [removingCustomIds, setRemovingCustomIds] = useState([]);
    const [affectedProductsDialogState, setAffectedProductsDialogState] = useState(initialAffectedProductsDialogState);

    const dispatch = useDispatch();
    const isMounted = useIsMounted();

    const [settingHighlighted, setSettingHighlighted] = useState([]);
    const [settingVariant, setSettingVariant] = useState([]);
    const [moving, setMoving] = useState(false);
    const [changeOrderDialogState, setChangeOrderDialogState] = useState(initChangeOrderDialogState);
    const [waitingForCommitting, setWaitingForCommitting] = useState({orders: false, highlighted: false, variance: false});
    const highlightedNamesRef = React.useRef([]);
    const orderedPropertyIdsRef = React.useRef([]);
    const variantPropertyIdsRef = React.useRef([]);

    const {configureNotebookTags} = useConfigureNotebook('manage-product-properties-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.manageProductProperties);
        configureBehavioralTool((tags) => tags.manageProductProperties);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Automatically fetches the cached industry when the component is mounted
     */
    useEffect(() => {
        if (!cachedIndustry?.id)
            return;
        void fetchIndustry({industryId: cachedIndustry.id});
    }, [cachedIndustry?.id, fetchIndustry]);

    /**
     * Automatically fetches the industry if there is only one industry available and it's not already fetched or selected
     */
    useEffect(() => {
        if (industries?.length === 1) {
            const industry = industries[0];
            void fetchIndustry({industryId: industry.id});
        }
    }, [fetchIndustry, industries]);


    const typeNames = React.useMemo(() => ({
        [ProductTechnicalPropertyTypeIds.text]: "Text",
        [ProductTechnicalPropertyTypeIds.number]: "Number",
        [ProductTechnicalPropertyTypeIds.date]: "Date",
        [ProductTechnicalPropertyTypeIds.boolean]: "Boolean",
        [ProductTechnicalPropertyTypeIds.decimal]: "Decimal",
        [ProductTechnicalPropertyTypeIds.money]: "Money",
        [ProductTechnicalPropertyTypeIds.fraction]: "Fraction",
        [ProductTechnicalPropertyTypeIds.color]: "Color",
    }), [])

    const properties = useMemo(() => {
        const ordered = [];
        for (const id of propertyExtraConfig.propertiesOrders) {
            const found = _properties.find((item) => item.id === id);
            if (found)
                ordered.push(found);
        }
        const rest = _properties.filter((item) => !ordered.includes(item));
        rest.sort((a, b) => a.title.localeCompare(b.title));
        ordered.push(...rest);
        const highlightedProperties = propertyExtraConfig.industryProperties.filter(e => propertyExtraConfig.highlightedProperties.includes(e.name))
        const variants = propertyExtraConfig.variants.map(id => propertyExtraConfig.industryProperties.find(ip => ip.id === id)).filter(e => !!e);
        return ordered.map((e, orderIndex) => ({
            ...e,
            orderIndex: orderIndex,
            isHighlighted: highlightedProperties.some(hp => hp.id === e.id),
            settingHighlighted: settingHighlighted.includes(propertyExtraConfig.industryProperties.find(ip => ip.id === e.id)?.name),
            settingVariant: settingVariant.includes(propertyExtraConfig.industryProperties.find(ip => ip.id === e.id)?.name),
            isVariant: variants.some(vp => vp.id === e.id),
            variantOrderIndex: variants.findIndex(vp => vp.id === e.id),
        }));
    }, [_properties, propertyExtraConfig.highlightedProperties, propertyExtraConfig.industryProperties, propertyExtraConfig.propertiesOrders, propertyExtraConfig.variants, settingHighlighted, settingVariant]);

    const varianceHierarchy = useMemo(() => {
        const variants = properties.filter(e => e.isVariant);
        variants.sort((a, b) => a.variantOrderIndex - b.variantOrderIndex);
        return variants;
    }, [properties]);

    /**
     * Searches for the properties that match the given keywords.
     * @type {function(*): *[]}
     */
    const getVisibleProperties = useCallback((keywords, keepAll = false) => {
        const keep = (e) => {
            return !keywords || (
                e.title?.toLowerCase().includes(keywords.toLowerCase())
                || e.description?.toLowerCase().includes(keywords.toLowerCase())
                || typeNames[e.typeId]?.toLowerCase().includes(keywords.toLowerCase())
            )
        }
        if (!keepAll) {
            return properties.filter(keep)
        }
        return properties.map(e => ({
            ...e,
            visible: keep(e),
        }));
    }, [properties, typeNames])

    const visibleProperties = React.useMemo(() => getVisibleProperties(searchQuery.keyword, true), [getVisibleProperties, searchQuery.keyword]);

    /**
     * Updates the selected property with its new values.
     * @type {(function(*): void)}
     */
    const onPropertyUpdated = useCallback((property) => {
        setProperties(prevState => prevState.map(e => e.id === property.id ? {...e, ...property} : e));
        if (updatePropertyDialogState.open && updatePropertyDialogState.property?.id === property.id) {
            setUpdatePropertyDialogState(prevState => ({...prevState, property: {...prevState.property, ...property}}));
        }
    }, [updatePropertyDialogState.open, updatePropertyDialogState.property?.id])

    /**
     * Fetches properties for the selected industry
     * @type {(function(): Promise<void>)}
     */
    const getProperties = useCallback(async (cancelToken) => {
        setLoadingProperties(true);
        const response = await Api.getEligibleIndustryPropertiesByIndustryId(industry?.id, cancelToken);
        if (!isMounted())
            return;
        if (!response?.aborted) {
            setLoadingProperties(false);
        }
        if (response?.resultFlag) {
            setProperties(response?.data?.map(e => ({
                id: e.id,
                title: e.title,
                description: e.description,
                typeId: e.industryPropertyTypeId,
                hasPredefinedValues: e.config?.hasPreDefinedValues ?? false,
                isMultiSelect: e.config?.isMultiSelect ?? false,
                isDefault: e.isDefault ?? false,
            })) ?? []);
        }
    }, [industry?.id, isMounted])

    /**
     * As soon as the component is mounted, fetch the properties of the industry.
     * - fetches the properties of the industry.
     */
    useEffect(() => {
        if (!industry?.id)
            return;
        const cancelTokenSource = axios.CancelToken.source();
        void getProperties(cancelTokenSource.token);
        return () => {
            cancelTokenSource.cancel();
        }
    }, [getProperties, industry?.id]);

    /**
     * Fetches the property extra configs of the industry.
     * @type {(function(*): Promise<void>)}
     */
    const fetchPropertyExtraConfigs = useCallback(async (cancelToken) => {
        const response = await Api.getIndustryPropertyExtraConfigs(industry?.id, cancelToken);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setPropertyExtraConfig({
                industryProperties: response.data?.industryProperties ?? [],
                highlightedProperties: response.data?.highlightedProperties ?? [],
                propertiesOrders: response.data?.propertiesOrders ?? [],
                variants: response.data?.variants ?? [],
            });
            highlightedNamesRef.current = response.data?.highlightedProperties ?? [];
            orderedPropertyIdsRef.current = response.data?.propertiesOrders ?? [];
            variantPropertyIdsRef.current = response.data?.variants ?? [];
        }
    }, [isMounted, industry?.id])

    /**
     * When the industry changes, fetch the property orders.
     */
    useEffect(() => {
        if (!industry?.id)
            return;
        const cancelTokenSource = axios.CancelToken.source();
        void fetchPropertyExtraConfigs(cancelTokenSource.token);
        return () => cancelTokenSource.cancel();
    }, [industry?.id, fetchPropertyExtraConfigs])

    /**
     * Opens the dialog for suggesting a new technical property
     * @type {(function(): void)}
     */
    const suggestStandardProperty = useCallback(() => {
        if (!operatorAccessLevels.packages.lookup.submitTechnicalSuggestions) {
            return;
        }
        setSuggestTechnicalPropertyDialogState({open: true});
    }, [operatorAccessLevels.packages.lookup.submitTechnicalSuggestions])

    /**
     * Opens the dialog for adding a custom technical property
     * @type {(function(): void)}
     */
    const addCustomProperty = useCallback(() => {
        setAddCustomPropertyDialogState({open: true});
    }, [])

    /**
     * Adds the newly created property to the list of properties
     * @type {(function(*): void)}
     */
    const onCustomPropertyCreated = useCallback((property) => {
        setProperties(prevState => {
            const newState = ([...prevState, property]);
            newState.sort((a, b) => {
                if (typeof a.orderIndex === 'number' && typeof b.orderIndex === 'number') {
                    return a.orderIndex - b.orderIndex;
                }
                return stringComparator(a.title, b.title);
            });
            return newState;
        });
        setPropertyExtraConfig(prevState => ({
            ...prevState,
            industryProperties: [...prevState.industryProperties, property],
        }));
    }, [])

    /**
     * Opens the dialog for updating the property.
     * @type {(function(*): void)}
     */
    const openUpdatePropertyDialog = useCallback((property) => {
        setUpdatePropertyDialogState({open: true, property});
    }, [])

    /**
     * Opens the dialog for managing the templates of the property.
     * @type {(function())|*}
     */
    const manageTemplates = useCallback(() => {
        setManageTechnicalPropertyTemplatesDialogState({open: true})
    }, [])

    /**
     * Gets the removal confirmation for the property
     * @type {(function(*, number=): void)}
     */
    const getRemoveConfirmation = useCallback((property, numPartsAffected = 0) => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            data: null,
            removedEntryTitle: `Property: "${property.title}" with ${numPartsAffected} affected product${numPartsAffected === 1 ? '' : 's'}`,
            onClose: async (confirmed) => {
                if (!confirmed)
                    return;
                setRemovingCustomIds(prevState => [...prevState, property.id]);
                const response = await Api.removeCustomIndustryProperty(property.id)
                if (!isMounted())
                    return;
                setRemovingCustomIds(prevState => prevState.filter(e => e !== property.id));
                if (response?.resultFlag) {
                    if (numPartsAffected) {
                        setAffectedProductsDialogState({
                            open: true,
                            productsList: response.data.affectedParts.map(e => e.partNo),
                            message: `The following ${numPartsAffected} part${numPartsAffected === 1 ? ' is' : 's are'} affected by the removal of the property: "${property.title}. You may download a CSV file for your reference"`,
                            fileTitle: `affected parts from removal of ${property.title}.csv`,
                        })
                    }
                    setProperties(prevState => prevState.filter(e => e.id !== property.id));
                }
                return response?.resultFlag ?? false
            }
        }));
    }, [dispatch, isMounted])

    /**
     * Toggles the highlighted status of the property with the given id.
     * @type {(function(*): Promise<void>)}
     */
    const toggleHighlighted = useCallback(async (property) => {
        const name = propertyExtraConfig.industryProperties.find(e => e.id === property.id)?.name;
        if (!name)
            return;
        setPropertyExtraConfig(prevState => ({
            ...prevState,
            highlightedProperties: prevState.highlightedProperties.includes(name)
                ? prevState.highlightedProperties.filter(e => e !== name)
                : [...prevState.highlightedProperties, name],
        }));
    }, [propertyExtraConfig.industryProperties])

    /**
     * Applies the highlighted status of the properties with the given names.
     * @type {(function(*): Promise<void>)}
     */
    const applyHighlighted = useCallback(async (names) => {
        setSettingHighlighted(names);
        const forApi = {
            industryId: industry?.id,
            highlightedProperties: names,
            variants: [],
            updatedProperties: ['HighlightedProperties'],
        }
        const response = await Api.saveIndustryPropertyExtraConfigs(forApi)
        if (!isMounted())
            return;
        setSettingHighlighted([]);
        setWaitingForCommitting(prevState => ({...prevState, highlighted: false}));
        if (response?.resultFlag) {
            setPropertyExtraConfig(prevState => ({
                ...prevState,
                highlightedProperties: names,
            }));
            highlightedNamesRef.current = names;
        }
    }, [industry?.id, isMounted])

    /**
     * Automatically applies the highlighted status of the properties when the properties change.
     * - This is to ensure that the highlighted status of the properties is applied after 3 seconds of the last change.
     */
    useEffect(() => {
        if (!properties.length) {
            setWaitingForCommitting(prevState => ({...prevState, highlighted: false}));
            return;
        }
        const names = propertyExtraConfig.highlightedProperties;
        const newSet = new Set(names);
        const committedSet = new Set(highlightedNamesRef.current);
        const notSame = names.length !== highlightedNamesRef.current.length || names.some((e) => !committedSet.has(e)) || highlightedNamesRef.current.some((e) => !newSet.has(e));
        if (!notSame) {
            setWaitingForCommitting(prevState => ({...prevState, highlighted: false}));
            return;
        }
        setWaitingForCommitting(prevState => ({...prevState, highlighted: true}));
        const timer = setTimeout(() => {
            void applyHighlighted(names)
        }, 3000);
        return () => clearTimeout(timer);
    }, [applyHighlighted, propertyExtraConfig.highlightedProperties, properties.length])

    /**
     * Toggle the order of the properties.
     * @type {function(*): void}
     */
    const toggleOrder = useCallback((properties) => setPropertyExtraConfig(prevState => ({
        ...prevState,
        propertiesOrders: properties.map(e => e.id),
    })), [])

    /**
     * Applies the order of the properties.
     * @type {(function(*): Promise<void>)}
     */
    const applyOrder = useCallback(async (orderIds) => {
        setMoving(true);
        const forApi = {
            industryId: industry?.id,
            propertiesOrders: orderIds,
            variants: [],
            updatedProperties: ['PropertiesOrders'],
        }
        const response = await Api.saveIndustryPropertyExtraConfigs(forApi)
        if (!isMounted())
            return;
        setMoving(false);
        setWaitingForCommitting(prevState => ({...prevState, orders: false}));
        if (response?.resultFlag) {
            setPropertyExtraConfig(prevState => ({
                ...prevState,
                propertiesOrders: orderIds,
            }));
            orderedPropertyIdsRef.current = orderIds;
        }
    }, [industry?.id, isMounted])

    /**
     * Applies the order of the properties when the properties change.
     * - This is to ensure that the order of the properties is applied after 3 seconds of the last change.
     */
    useEffect(() => {
        if (!properties.length) {
            setWaitingForCommitting(prevState => ({...prevState, orders: false}));
            return;
        }
        const orderIds = propertyExtraConfig.propertiesOrders;
        if (orderIds.join(',') === orderedPropertyIdsRef.current.join(',')) {
            setWaitingForCommitting(prevState => ({...prevState, orders: false}));
            return;
        }
        setWaitingForCommitting(prevState => ({...prevState, orders: true}));
        const timer = setTimeout(() => {
            void applyOrder(orderIds)
        }, 3000);
        return () => clearTimeout(timer);
    }, [applyOrder, propertyExtraConfig.propertiesOrders, properties])

    /**
     * Opens the dialog for changing the order of the property.
     * @type {(function(*): void)}
     */
    const openReorderPropertyDialog = useCallback((oldIndex) => {
        setChangeOrderDialogState({
            ...initChangeOrderDialogState,
            open: true,
            close: () => setChangeOrderDialogState(initChangeOrderDialogState),
            orderIndex: oldIndex + 1,
            length: properties.length,
            reorder: (newIndex) => {
                const newProperties = Utils.reorderList(Array.from(properties), oldIndex, newIndex - 1)
                toggleOrder(newProperties);
                return {resultFlag: true};
            },
            message: 'Please select the new order of the property. This will change the order of the property in the list. and shift the other properties accordingly.',
        });
    }, [properties, toggleOrder])

    /**
     * Opens the dialog for changing the variant order of the property.
     * @type {(function(*): void)}
     */
    const openVariantReorderDialog = useCallback((oldIndex) => {
        setChangeOrderDialogState({
            ...initChangeOrderDialogState,
            open: true,
            close: () => setChangeOrderDialogState(initChangeOrderDialogState),
            orderIndex: oldIndex + 1,
            length: properties.filter(e => e.isVariant).length,
            reorder: (newIndex) => {
                const variants = propertyExtraConfig.variants;
                const newProperties = Utils.reorderList(Array.from(variants), oldIndex, newIndex - 1)
                setPropertyExtraConfig(prevState => ({
                    ...prevState,
                    variants: newProperties,
                }))
                return {resultFlag: true};
            },
            message: 'Please select the new order of the option property. This will change the order of the option property in the list. and shift the other option properties accordingly.',
        });
    }, [properties, propertyExtraConfig.variants])

    /**
     * Toggle the variance of the property.
     * @type {(function(*): void)|*}
     */
    const toggleVariance = useCallback((property) => {
        const id = propertyExtraConfig.industryProperties.find(e => e.id === property.id)?.id;
        if (!id)
            return;
        setPropertyExtraConfig(prevState => ({
            ...prevState,
            variants: prevState.variants.includes(id)
                ? prevState.variants.filter(e => e !== id)
                : [...prevState.variants, id],
        }));
    }, [propertyExtraConfig.industryProperties]);

    /**
     * Applies the variance status of the properties with the given names.
     * @type {(function(*): Promise<void>)}
     */
    const applyVariance = useCallback(async (ids) => {
        setSettingVariant(ids);
        const forApi = {
            industryId: industry?.id,
            variants: ids,
            updatedProperties: ['Variants'],
        }
        const response = await Api.saveIndustryPropertyExtraConfigs(forApi)
        if (!isMounted())
            return;
        setSettingVariant([]);
        setWaitingForCommitting(prevState => ({...prevState, variance: false}));
        if (response?.resultFlag) {
            setPropertyExtraConfig(prevState => ({
                ...prevState,
                variants: ids,
            }));
            variantPropertyIdsRef.current = ids;
        }
    }, [industry?.id, isMounted])

    /**
     * Automatically applies the variance status of the properties when the properties change.
     * - This is to ensure that the variant status of the properties is applied after 3 seconds of the last change.
     */
    useEffect(() => {
        if (!properties.length) {
            setWaitingForCommitting(prevState => ({...prevState, variance: false}));
            return;
        }
        const ids = propertyExtraConfig.variants;
        // here we can not use new Set(ids) because the order of the properties is important.
        const notSame = ids.length !== variantPropertyIdsRef.current.length || ids.some((e, index) => variantPropertyIdsRef.current[index] !== e);
        if (!notSame) {
            setWaitingForCommitting(prevState => ({...prevState, variance: false}));
            return;
        }
        setWaitingForCommitting(prevState => ({...prevState, variance: true}));
        const timer = setTimeout(() => {
            void applyVariance(ids)
        }, 3000);
        return () => clearTimeout(timer);
    }, [applyVariance, propertyExtraConfig.variants, properties.length])

    return (
        <>
            <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBlockEnd: 4}}>
                <Typography variant={'h6'} fontWeight={600}>
                    Product Properties Management
                </Typography>
                {
                    !loadingIndustries && !hasNoIndustries && !!industry?.id &&
                    <Box sx={{display: 'flex', flexDirection: 'row', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap'}}>
                        <Button
                            disabled={loadingProperties}
                            className='d-flex align-items-center justify-content-center'
                            variant={'outlined'}
                            color={'primary'}
                            onClick={() => manageTemplates()}
                        >
                            <ListAltOutlined className={'mr-2'}/>
                            <p>
                                Manage Templates
                            </p>
                        </Button>
                        <Button
                            disabled={loadingProperties}
                            className='d-flex align-items-center justify-content-center'
                            variant={'outlined'}
                            color={'primary'}
                            onClick={() => addCustomProperty()}
                        >
                            <Add className={'mr-2'}/>
                            <p>
                                Add Custom Property
                            </p>
                        </Button>
                        {
                            operatorAccessLevels.packages.lookup.submitTechnicalSuggestions &&
                            <Button
                                disabled={loadingProperties}
                                className='d-flex align-items-center justify-content-center'
                                variant={'outlined'}
                                color={'primary'}
                                onClick={() => suggestStandardProperty()}
                            >
                                <Add className={'mr-2'}/>
                                <p>
                                    Suggest Standard Property
                                </p>
                            </Button>
                        }
                    </Box>
                }
            </Box>
            {
                (!!loadingIndustries || industries?.length > 1) &&
                <Box sx={{width: '100%', marginBlock: 3}}>
                    <Tabs value={industry?.id} onChange={(e, industryId) => fetchIndustry({industryId})}>
                        {
                            !loadingIndustries &&
                            industries?.map((industry) => (
                                <Tab key={industry.id} value={industry.id} label={industry.title}/>
                            ))
                        }
                        {
                            loadingIndustries &&
                            Array.from({length: 3}).map((_, e) => (
                                <Tab
                                    key={`loading_${e}`}
                                    label={<Skeleton sx={{minHeight: 24, transform: 'none', width: '100%'}}/>}
                                    minWidth={'20%'}
                                />
                            ))
                        }
                    </Tabs>
                </Box>
            }
            {
                hasNoIndustries && !loadingIndustries &&
                <p className={'text-sm text-color-3 text-center my-5 py-5 w-100'}>
                    <WarningOutlined
                        color={'warning'}
                        sx={{display: 'inline-block', marginInlineEnd: 0.5}}
                    />
                    There are no industries in your organization. Please add an industry to continue.
                    <IconButton
                        color={'paper.primary'}
                        component={Link}
                        to={routes.settings.industries}
                        sx={{display: 'inline-block', marginInlineStart: 0.5}}
                    >
                        <OpenInNew/>
                    </IconButton>
                </p>
            }
            {
                loadingIndustries &&
                <LoadingLottieAnimation width={'min(100%, 500px)'} className={''}/>
            }
            {
                !loadingIndustries && !hasNoIndustries && !industry?.id &&
                <>
                    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', marginBlock: 12}}>
                        <Typography variant={'body2'} color={'text.secondary'} fontWeight={600} textAlign={'center'}>
                            Please select an industry from the list above to view and manage its properties.
                        </Typography>
                    </Box>
                </>
            }
            {
                !loadingIndustries && !hasNoIndustries && !!industry?.id &&
                <>
                    <Box sx={{backgroundColor: 'background.paper', borderColor: 'divider', borderRadius: 1, padding: 3, display: 'flex', flexDirection: 'column', gap: 2}}>
                        {
                            operatorAccessLevels.packages.lookup.productVariants &&
                            <>
                                <Typography variant={'body2'} marginBlockEnd={1}>
                                    Making a property a "product option" means that the property will be used to differentiate product variants in your catalog. This allows customers to select
                                    different variations of a product based on the options you provide.
                                </Typography>
                                {
                                    !!varianceHierarchy.length &&
                                    <Box sx={{marginBlockEnd: 2, display: 'flex', flexDirection: 'row', gap: 0.5, flexWrap: 'wrap', alignItems: 'center'}}>
                                        <Typography variant={'body2'} color={'text.secondary'} fontWeight={600} sx={{width: '100%'}} gutterBottom>
                                            Current Options Hierarchy:
                                        </Typography>
                                        {
                                            varianceHierarchy.map((property, i, array) => (
                                                <React.Fragment key={property.id}>
                                                    <Chip
                                                        label={property.title}
                                                        variant={'filled'}
                                                        color={'primary'}
                                                        size={'medium'}
                                                        sx={{
                                                            fontWeight: 600,
                                                        }}
                                                    />
                                                    {
                                                        i < array.length - 1 &&
                                                        <ArrowRightAltOutlined/>
                                                    }
                                                </React.Fragment>
                                            ))
                                        }
                                    </Box>
                                }
                                {
                                    !varianceHierarchy.length &&
                                    <Typography variant={'body2'} color={'text.secondary'} fontStyle={'italic'} marginBlockEnd={1}>
                                        No product options defined yet.
                                    </Typography>
                                }
                            </>
                        }
                        <TextField
                            label={"Search Properties"}
                            name={'search'}
                            fullWidth
                            value={keywordValue}
                            onChange={(e) => {
                                const value = e.target.value;
                                // Update the visible input immediately so typing feels instant
                                setKeywordValue(value);
                                startTransition(() => {
                                    setSearchQuery(prevState => ({...prevState, keyword: value}));
                                });
                            }}
                        />
                        <Typography variant={'body2'} marginBlockEnd={1}>
                            You can reorder the properties of the industry by dragging and dropping them. The order of the properties will be reflected in technical information
                            section of the product page. You can also choose to show or hide a property in the lookup results by toggling the checkbox next to the property.
                        </Typography>
                        {
                            loadingProperties &&
                            Array.from({length: 20}).map((_, i) => (
                                <Skeleton
                                    key={`loading-${i}`}
                                    variant="rectangular"
                                    width={'100%'}
                                    sx={(theme) => ({minHeight: 80, width: '100%', borderRadius: (theme).vars?.shape.borderRadius})}
                                />
                            ))
                        }
                        <ReactSortable
                            list={properties}
                            setList={toggleOrder}
                            sort
                            disabled={moving || loadingProperties}
                            handle={'.handle'}
                            ghostClass={'ghost'}
                            animation={210}
                            style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                flexGrow: 1,
                                gap: 16,
                                ...(!loadingProperties && {
                                    minHeight: 100,
                                }),
                            }}
                        >
                            {
                                !loadingProperties &&
                                visibleProperties.map(property => (
                                    <PropertyCard
                                        moving={moving}
                                        key={property.id}
                                        onUpdate={openUpdatePropertyDialog}
                                        property={property}
                                        typeNames={typeNames}
                                        openReorderDialog={openReorderPropertyDialog}
                                        toggleHighlighted={toggleHighlighted}
                                        openVariantReorderDialog={openVariantReorderDialog}
                                        toggleVariance={toggleVariance}
                                        anySettingVariant={settingVariant.length > 0}
                                        anySettingHighlighted={settingHighlighted.length > 0}
                                        {...(!property.isDefault && {
                                            onRemove: getRemoveConfirmation,
                                            removing: removingCustomIds.includes(property.id),
                                        })}
                                    />
                                ))
                            }
                        </ReactSortable>
                        {
                            !loadingProperties && !visibleProperties.length &&
                            <Typography variant={'body2'} color={'text.secondary'} textAlign={'center'} marginBlockEnd={6}>
                                No properties found for this industry. Please add properties to the industry to see them here.
                            </Typography>
                        }
                    </Box>
                    {
                        (waitingForCommitting.orders || waitingForCommitting.highlighted || waitingForCommitting.variance) &&
                        <PanelFooter>
                            <Typography variant={'body2'} sx={{color: 'text.secondary', marginBlock: 2}}>
                                You have made changes to
                                the {[
                                waitingForCommitting.orders ? 'order' : '',
                                waitingForCommitting.highlighted ? 'highlighting' : '',
                                waitingForCommitting.variance ? 'variance' : '',
                            ].filter(e => e).join(' and ')}. The
                                changes will be applied in a few seconds. Please do not close this page.
                            </Typography>
                        </PanelFooter>
                    }
                </>
            }
            <UpsertProductTechnicalPropertyDialog
                open={addCustomPropertyDialogState.open}
                onUpsert={onCustomPropertyCreated}
                onClose={() => setAddCustomPropertyDialogState(initialAddCustomPropertyDialogState)}
                industryId={industry?.id}
            />
            <UpsertProductTechnicalPropertyDialog
                open={updatePropertyDialogState.open}
                property={updatePropertyDialogState.property}
                onUpsert={onPropertyUpdated}
                onClose={() => setUpdatePropertyDialogState(initialUpdatePropertyDialogState)}
                industryId={industry?.id}
            />
            {
                operatorAccessLevels.packages.lookup.submitTechnicalSuggestions &&
                <SuggestTechnicalPropertyDialog
                    open={suggestTechnicalPropertyDialogState.open}
                    close={() => setSuggestTechnicalPropertyDialogState(initialSuggestTechnicalPropertyDialogState)}
                    industryId={industry?.id}
                />
            }
            <DownloadAffectedProductsDialog
                open={affectedProductsDialogState.open}
                onClose={() => setAffectedProductsDialogState(initialAffectedProductsDialogState)}
                productList={affectedProductsDialogState.productsList}
                message={affectedProductsDialogState.message}
                fileTitle={affectedProductsDialogState.fileTitle}
            />
            <ManageTechnicalPropertyTemplatesDialog
                open={manageTechnicalPropertyTemplatesDialogState.open}
                setOpen={() => setManageTechnicalPropertyTemplatesDialogState(initialManageTechnicalPropertyTemplatesDialogState)}
                industryId={industry?.id}
                properties={properties}
                loadingProperties={loadingProperties}
                typeNames={typeNames}
                getVisibleProperties={getVisibleProperties}
            />
            <ChangeOrderDialog
                open={changeOrderDialogState.open}
                close={changeOrderDialogState.close}
                orderIndex={changeOrderDialogState.orderIndex}
                length={changeOrderDialogState.length}
                reorder={changeOrderDialogState.reorder}
                message={changeOrderDialogState.message}
            />
        </>
    );
}

export default ManageProductPropertiesView;


const PropertyCard = ({
                          moving,
                          property,
                          onUpdate,
                          onRemove,
                          removing,
                          typeNames,
                          openReorderDialog,
                          toggleHighlighted,
                          toggleVariance,
                          openVariantReorderDialog,
                          anySettingHighlighted,
                          anySettingVariant,
                      }) => {
    const [loadingAffectedProducts, setLoadingAffectedProducts] = useState(false);
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();

    /**
     * Removes the property but first fetches the number of affected products.
     * @type {(function(): Promise<void>)}
     */
    const removeProperty = useCallback(async () => {
        setLoadingAffectedProducts(true);
        const response = await Api.getIndustryPropertyRelatedPartsCount(property.id)
        if (!isMounted())
            return;
        setLoadingAffectedProducts(false);
        onRemove(property, response?.data?.numberOfUsage ?? 0);
    }, [isMounted, onRemove, property])

    const propertyTypeTitle = useMemo(() => typeNames[property.typeId], [property.typeId, typeNames]);

    return (
        <>
            <Card
                variant={'outlined'}
                sx={{padding: 2, ...(!property.visible && {display: 'none'})}}
                className={'manage-product-technical-properties-dialog-card w-100'}
            >
                <Grid container spacing={2}>
                    <Grid xs={(onUpdate || onRemove) ? true : 12} sx={{display: 'flex', alignItems: 'center', gap: 0.5, p: 0}}>
                        <Fade in={!moving}>
                            <IconButton color={'paper.primary'} className={'handle'}>
                                <DragHandleRounded/>
                            </IconButton>
                        </Fade>
                        <Box
                            sx={{
                                borderRadius: '50%',
                                width: 34,
                                height: 34,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'background.default',
                                color: 'text.primary',
                                cursor: 'pointer',
                            }}
                            onClick={() => openReorderDialog(property.orderIndex)}
                        >
                            <Typography variant={'body2'} fontWeight={600}>
                                {property.orderIndex + 1}
                            </Typography>
                        </Box>
                        <Typography variant={'body1'} sx={{marginInlineStart: 0.5}}>
                            {property.title ?? ""}
                            <Typography
                                component={'span'}
                                sx={{display: 'inline-block', marginInlineStart: 1}}
                                variant={'body2'}
                                color={'secondary.main'}
                            >
                                ({propertyTypeTitle})
                            </Typography>
                        </Typography>
                    </Grid>
                    {
                        (onUpdate || onRemove) &&
                        <Grid xs={'auto'} className={'d-flex align-items-center justify-content-end'} sx={{p: 0}}>
                            {
                                onUpdate &&
                                <IconButton color={'paper.primary'} size={'small'} onClick={() => onUpdate(property)}>
                                    <EditOutlined/>
                                </IconButton>
                            }
                            {
                                onRemove &&
                                <IconButton color={'paper.error'} size={'small'} disabled={removing || loadingAffectedProducts} onClick={() => removeProperty()}>
                                    {loadingAffectedProducts || removing ? <CircularProgress size={20} color={'error'}/> : <DeleteOutlined/>}
                                </IconButton>
                            }
                            {
                                property.isDefault &&
                                <Card
                                    variant={'outlined'}
                                    sx={{
                                        marginInlineStart: 1,
                                        padding: '2px 8px', borderRadius: 1,
                                        backgroundColor: theme => `rgba(${theme.vars.palette.tertiary.mainChannel} / ${theme.vars.palette.action.selectedOpacity})`,
                                        borderColor: 'tertiary.main'
                                    }}
                                >
                                    <Typography variant={'caption'} color={'tertiary.main'} fontWeight={600}>
                                        Default
                                    </Typography>
                                </Card>
                            }
                        </Grid>
                    }
                    <Grid xs={12}>
                        <Typography variant={'body2'}>
                            Description: {property.description ?? "--"}
                        </Typography>
                    </Grid>
                    <Grid xs={12} className={'d-flex align-items-center flex-wrap justify-content-between'} sx={{gap: 1, p: 0}}>
                        <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1}}>
                            <div className={classnames('checkmark-container')}>
                                {
                                    property.hasPredefinedValues
                                        ? <SaveIcon className={classnames(`save`, "mr-1")}/>
                                        : <CancelIcon className={classnames(`cancel`, "mr-1")}/>
                                }
                                <Typography variant={'caption'}>
                                    Has Predefined values
                                </Typography>
                            </div>
                            <div className={classnames('checkmark-container')}>
                                {
                                    property.isMultiSelect
                                        ? <SaveIcon className={classnames(`save`, "mr-1")}/>
                                        : <CancelIcon className={classnames(`cancel`, "mr-1")}/>
                                }
                                <Typography variant={'caption'}>
                                    Multi-select
                                </Typography>
                            </div>
                        </Box>
                        <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 1}}>
                            {
                                property.isDefault &&
                                <FormControlLabel
                                    label={property.settingHighlighted ? 'Processing...' : 'Show in lookup results'}
                                    sx={{margin: 0}}
                                    labelPlacement={'start'}
                                    control={
                                        <Checkbox
                                            disabled={!!anySettingHighlighted}
                                            sx={{paddingBlock: 0.75}}
                                            disableRipple
                                            checked={property.isHighlighted}
                                            onChange={() => toggleHighlighted(property)}
                                        />
                                    }
                                />
                            }
                            {
                                operatorAccessLevels.packages.lookup.productVariants &&
                                <Card
                                    variant={'outlined'}
                                    onClick={() => toggleVariance(property)}
                                    sx={{
                                        padding: 1,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 0.5,
                                        transition: theme => theme.transitions.create(['background-color', 'border-color', 'border-width'], {duration: theme.transitions.duration.short}),
                                        ...((!!anySettingVariant || !operatorAccessLevels.packages.lookup.manageProductVariants) && {
                                            pointerEvents: 'none',
                                        }),
                                        ...(property.isVariant && {
                                            backgroundColor: theme => `rgba(${theme.vars.palette.primary.mainChannel} / 0.3)`,
                                            borderColor: 'primary.main',
                                            borderWidth: 1,
                                        }),
                                        ...(!property.isVariant && {
                                            backgroundColor: 'transparent',
                                            borderColor: 'divider',
                                            borderStyle: 'dashed',
                                        }),
                                    }}
                                >
                                    {
                                        property.isVariant &&
                                        <Box
                                            sx={{
                                                borderRadius: '50%',
                                                aspectRatio: 1,
                                                height: 20,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: 'background.default',
                                                color: 'text.primary',
                                                cursor: 'pointer',
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                openVariantReorderDialog(property.variantOrderIndex)
                                            }}
                                        >
                                            <Typography variant={'body2'} fontWeight={600}>
                                                {property.variantOrderIndex + 1}
                                            </Typography>
                                        </Box>
                                    }
                                    <Typography variant={'body2'} color={property.isVariant ? 'primary.main' : 'text.secondary'} fontWeight={property.isVariant ? 600 : 500}>
                                        {
                                            property.settingVariant
                                                ? 'Processing...'
                                                : property.isVariant ? 'Product Option' : 'Make Product Option'
                                        }
                                    </Typography>
                                </Card>
                            }
                        </Box>
                    </Grid>
                </Grid>
            </Card>
        </>
    );
}